import { format, getDay, isSameDay } from 'date-fns';
import { cn } from '@/lib/cn';
import type { CalendarCell } from '@/lib/calendarMonth';
import type { CalendarVisualItem } from '@/components/calendar/views/WeekTimeGrid';

const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const MAX_VISIBLE = 3;

type Props = {
  monthTitle: string;
  cells: CalendarCell[];
  items: CalendarVisualItem[];
  selectedDate: Date | null;
  attendanceByDay?: Record<string, 'present' | 'absent' | 'weekoff' | 'holiday'> | null;
  onSelectDate: (date: Date, inCurrentMonth: boolean) => void;
  onItemClick?: (item: CalendarVisualItem, date: Date) => void;
  onMoreClick?: (date: Date) => void;
};

/** Accent dot per item kind — the title stays neutral so dense days remain readable. */
const KIND_DOT: Record<CalendarVisualItem['kind'], string> = {
  event: 'bg-blue-500',
  meeting: 'bg-fuchsia-500',
  task: 'bg-amber-500',
  note: 'bg-emerald-500',
  'org-event': 'bg-indigo-500',
  holiday: 'bg-rose-500',
  schedule: 'bg-slate-400',
  'pms-task': 'bg-violet-500',
  'pms-project': 'bg-cyan-500',
};

const ATTENDANCE: Record<'present' | 'absent' | 'weekoff' | 'holiday', { dot: string; label: string }> = {
  present: { dot: 'bg-emerald-500', label: 'Present' },
  absent: { dot: 'bg-rose-500', label: 'Absent' },
  weekoff: { dot: 'bg-slate-400', label: 'Week off' },
  holiday: { dot: 'bg-indigo-500', label: 'Holiday' },
};

export function MonthViewGrid({ cells, items, selectedDate, attendanceByDay, onSelectDate, onItemClick, onMoreClick }: Props) {
  const byDay = new Map<string, CalendarVisualItem[]>();
  for (const item of items) {
    const list = byDay.get(item.dateKey) ?? [];
    list.push(item);
    byDay.set(item.dateKey, list);
  }
  for (const [, list] of byDay) {
    list.sort((a, b) => a.startsAt.localeCompare(b.startsAt));
  }

  const rows = Math.max(1, Math.ceil(cells.length / 7));

  return (
    <section className="flex h-full min-h-0 flex-col overflow-hidden rounded-[28px] border border-border/60 bg-card p-2 shadow-[0_1px_2px_rgba(15,23,42,0.04),0_24px_48px_-24px_rgba(15,23,42,0.18)] sm:p-3">
      <div className="grid grid-cols-7 pb-1">
        {WEEKDAYS.map((d, i) => (
          <div
            key={d}
            className={cn(
              'py-2 text-center text-[11px] font-semibold uppercase tracking-[0.18em]',
              i >= 5 ? 'text-muted-foreground/50' : 'text-muted-foreground'
            )}
          >
            <span className="sm:hidden">{d[0]}</span>
            <span className="hidden sm:inline">{d}</span>
          </div>
        ))}
      </div>

      <div
        className="grid flex-1 grid-cols-7 gap-1 sm:gap-1.5"
        style={{ gridTemplateRows: `repeat(${rows}, minmax(92px, 1fr))` }}
      >
        {cells.map((cell) => {
          const key = format(cell.date, 'yyyy-MM-dd');
          const dayItems = byDay.get(key) ?? [];
          const visible = dayItems.slice(0, MAX_VISIBLE);
          const overflow = dayItems.length - visible.length;
          const attendance = typeof attendanceByDay === 'object' && attendanceByDay ? attendanceByDay[key] : undefined;
          const isSelected = selectedDate ? isSameDay(selectedDate, cell.date) : false;
          const dow = getDay(cell.date);
          const isWeekend = dow === 0 || dow === 6;

          return (
            <div
              key={key}
              role="button"
              tabIndex={0}
              aria-label={format(cell.date, 'EEEE, d MMMM yyyy')}
              aria-pressed={isSelected}
              onClick={() => onSelectDate(cell.date, cell.inCurrentMonth)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onSelectDate(cell.date, cell.inCurrentMonth);
                }
              }}
              className={cn(
                'group relative flex min-w-0 cursor-pointer flex-col gap-1 rounded-2xl p-1.5 text-left outline-none transition-all duration-200 sm:p-2',
                'focus-visible:ring-2 focus-visible:ring-ring/40',
                cell.inCurrentMonth
                  ? isWeekend
                    ? 'bg-muted/50 hover:bg-muted'
                    : 'bg-muted/25 hover:bg-muted/70'
                  : 'bg-transparent opacity-45 hover:opacity-80 hover:bg-muted/30',
                isSelected && 'bg-primary/[0.06] opacity-100 ring-1 ring-primary/30 hover:bg-primary/10'
              )}
            >
              <div className="flex items-center justify-between">
                <span
                  className={cn(
                    'flex h-7 min-w-7 items-center justify-center rounded-full px-1 text-[13px] font-semibold tabular-nums transition-colors',
                    cell.isToday
                      ? 'bg-primary text-primary-foreground shadow-[0_6px_14px_-4px_hsl(var(--primary)/0.55)]'
                      : isSelected
                        ? 'text-primary'
                        : 'text-foreground/80 group-hover:text-foreground'
                  )}
                >
                  {format(cell.date, 'd')}
                </span>

                {attendance && ATTENDANCE[attendance] && (
                  <span
                    className="flex items-center gap-1 rounded-full bg-background/70 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-muted-foreground"
                    title={ATTENDANCE[attendance].label}
                  >
                    <span className={cn('h-1.5 w-1.5 rounded-full', ATTENDANCE[attendance].dot)} />
                    <span className="hidden xl:inline">{ATTENDANCE[attendance].label}</span>
                  </span>
                )}
              </div>

              <div className="flex min-h-0 flex-col gap-0.5">
                {visible.map((item) => (
                  <button
                    key={item.id}
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      onItemClick?.(item, cell.date);
                    }}
                    className="flex w-full min-w-0 items-center gap-1.5 rounded-lg px-1.5 py-[3px] text-left text-[11px] font-medium text-foreground/80 transition-colors hover:bg-background hover:text-foreground hover:shadow-sm"
                    title={item.title}
                  >
                    <span className={cn('h-1.5 w-1.5 shrink-0 rounded-full', KIND_DOT[item.kind])} />
                    <span className="truncate">{item.title}</span>
                  </button>
                ))}
                {overflow > 0 && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      onMoreClick?.(cell.date);
                    }}
                    className="w-fit rounded-md px-1.5 py-0.5 text-[10px] font-semibold text-muted-foreground transition-colors hover:text-foreground"
                  >
                    +{overflow} more
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}
